// Headless core: the whole selection state machine, no JSX. Anything needing
// different presentation consumes this hook instead of forking the component.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addMonths,
  clampDate,
  composeDisabled,
  formatHm,
  isAfterDay,
  isBeforeDay,
  isMatch,
  isSameDay,
  isSameMonth,
  isValidDate,
  orderRange,
  parseHm,
  setTime,
  startOfMonth,
  today,
  type DateMatcher,
  type DateRangeValue,
  type WeekStart,
} from "./dateCore";
import { toFormValue } from "./codecs";
import type { LocaleTag } from "./format";
import { resolvePreset } from "./presets";
import { VARIANT_CONFIG, type VariantConfig } from "./variantConfig";
import type {
  DateTimePickerCodec,
  DateTimePickerPreset,
  DateTimePickerVariant,
  DayState,
  InternalValue,
  ValueTypeName,
} from "./types";

export interface UseDateTimePickerOptions {
  variant?: DateTimePickerVariant;
  valueType?: ValueTypeName;
  /** Controlled value, in the wire format named by `valueType`. */
  value?: unknown;
  defaultValue?: unknown;
  onValueChange?: (value: never) => void;
  codec?: DateTimePickerCodec;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: DateMatcher | DateMatcher[];
  weekStartsOn?: WeekStart;
  locale?: LocaleTag;
  displayFormat?: string;
  closeOnSelect?: boolean;
  numberOfMonths?: number;
  minuteStep?: number;
  /** Controlled popover state. Omit to let the hook own it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  clearable?: boolean;
}

export interface UseDateTimePickerReturn {
  variant: DateTimePickerVariant;
  config: VariantConfig;
  /** Committed value, `Date`-based. Never a half-finished range. */
  selected: InternalValue;
  /** What the calendar should paint, including an in-progress range preview. */
  preview: InternalValue;
  hasValue: boolean;
  displayValue: string;
  /** Flattened string for a hidden input inside a native form. */
  formValue: string;

  open: boolean;
  setOpen: (open: boolean) => void;

  /** First visible month. `months` expands it by `numberOfMonths`. */
  viewMonth: Date;
  months: Date[];
  setViewMonth: (month: Date) => void;
  goToMonth: (delta: number) => void;
  canGoPrev: boolean;
  canGoNext: boolean;
  canGoPrevYear: boolean;
  canGoNextYear: boolean;
  /** Month bounds for the caption's year dropdown. */
  navStart: Date;
  navEnd: Date;

  hoverDay?: Date;
  setHoverDay: (date?: Date) => void;
  focusedDay: Date;
  setFocusedDay: (date: Date) => void;
  isPickingRange: boolean;

  selectDay: (date: Date) => void;
  selectTime: (hours: number, minutes: number) => void;
  applyPreset: (preset: DateTimePickerPreset) => void;
  clear: () => void;

  isDisabledDay: (date: Date) => boolean;
  getDayState: (date: Date, month: Date) => DayState;
  /** Current time of day for the time panel, as `[hours, minutes]`. */
  timeParts: [number, number];
}

/** How far the year dropdown reaches when no min/max is given. */
const DEFAULT_YEARS_BACK = 31;
const DEFAULT_YEARS_FORWARD = 10;

/** The day a fresh calendar should open on, given the current value. */
function anchorOf(value: InternalValue): Date | undefined {
  if (isValidDate(value)) return value;
  if (Array.isArray(value)) return value.find((entry) => isValidDate(entry));
  if (value && typeof value === "object" && "from" in value) {
    const range = value as DateRangeValue;
    return isValidDate(range.to) ? range.to : range.from;
  }
  return undefined;
}

export function useDateTimePicker(options: UseDateTimePickerOptions = {}): UseDateTimePickerReturn {
  const {
    variant = "single",
    valueType = "date",
    value,
    defaultValue,
    onValueChange,
    codec: codecOverride,
    minDate,
    maxDate,
    disabledDates,
    weekStartsOn = 0,
    locale,
    displayFormat,
    closeOnSelect,
    numberOfMonths = 1,
    open: controlledOpen,
    onOpenChange,
    clearable = false,
  } = options;

  const config = VARIANT_CONFIG[variant];

  /* A time-only variant keeps a plain "HH:mm" string rather than a Date, and
     clears to "" rather than undefined. Asked of the config rather than the
     variant name so every such variant answers the same way. */
  const isTimeOnly = config.hasTime && !config.hasCalendar;
  const codec = codecOverride ?? config.codec;
  const pattern = displayFormat ?? config.defaultFormat;
  const shouldCloseOnSelect = closeOnSelect ?? config.closeOnSelect;

  /* Value */

  const isControlled = value !== undefined;
  const [uncontrolled, setUncontrolled] = useState<InternalValue>(() =>
    codec.parse(defaultValue, valueType),
  );

  const selected = useMemo<InternalValue>(
    () => (isControlled ? codec.parse(value, valueType) : uncontrolled),
    [isControlled, codec, value, valueType, uncontrolled],
  );

  const emit = useCallback(
    (next: InternalValue) => {
      if (!isControlled) setUncontrolled(next);
      (onValueChange as ((v: unknown) => void) | undefined)?.(codec.serialize(next, valueType));
    },
    [isControlled, onValueChange, codec, valueType],
  );

  /* Open state */

  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (controlledOpen === undefined) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [controlledOpen, onOpenChange],
  );

  /* Navigation */

  const navStart = useMemo(
    () => startOfMonth(minDate ?? new Date(new Date().getFullYear() - DEFAULT_YEARS_BACK, 0, 1)),
    [minDate],
  );
  const navEnd = useMemo(
    () =>
      startOfMonth(maxDate ?? new Date(new Date().getFullYear() + DEFAULT_YEARS_FORWARD, 11, 1)),
    [maxDate],
  );

  const initialMonth = useCallback((): Date => {
    const anchor = anchorOf(selected) ?? clampDate(today(), minDate, maxDate);
    const first = startOfMonth(anchor);
    // Multi-month: the meaningful month goes right, so panes read [context, current].
    const shifted = numberOfMonths > 1 ? addMonths(first, -(numberOfMonths - 1)) : first;
    return clampMonth(shifted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, minDate, maxDate, numberOfMonths]);

  function clampMonth(month: Date): Date {
    const start = startOfMonth(month);
    if (start < navStart) return navStart;
    // The LAST visible pane must not pass navEnd.
    const lastVisible = addMonths(start, numberOfMonths - 1);
    if (lastVisible > navEnd) return addMonths(navEnd, -(numberOfMonths - 1));
    return start;
  }

  const [viewMonth, setViewMonthState] = useState<Date>(initialMonth);

  const setViewMonth = useCallback(
    (month: Date) => {
      const nextMonth = clampMonth(month);
      setViewMonthState(nextMonth);
      setFocusedDayState((current) => {
        const lastDayOfTargetMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0).getDate();
        const dayNum = Math.min(current.getDate(), lastDayOfTargetMonth);
        return new Date(nextMonth.getFullYear(), nextMonth.getMonth(), dayNum);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clampMonth's closure is stable per render
    [navStart, navEnd, numberOfMonths],
  );

  const goToMonth = useCallback(
    (delta: number) =>
      setViewMonthState((current) => {
        const nextMonth = clampMonth(addMonths(current, delta));
        setFocusedDayState((curDay) => {
          const lastDayOfTargetMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0).getDate();
          const dayNum = Math.min(curDay.getDate(), lastDayOfTargetMonth);
          return new Date(nextMonth.getFullYear(), nextMonth.getMonth(), dayNum);
        });
        return nextMonth;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navStart, navEnd, numberOfMonths],
  );

  const months = useMemo(
    () => Array.from({ length: Math.max(1, numberOfMonths) }, (_, i) => addMonths(viewMonth, i)),
    [viewMonth, numberOfMonths],
  );

  const canGoPrev = startOfMonth(viewMonth) > navStart;
  const canGoNext = addMonths(startOfMonth(viewMonth), numberOfMonths - 1) < navEnd;
  const canGoPrevYear = startOfMonth(viewMonth) > navStart;
  const canGoNextYear = addMonths(startOfMonth(viewMonth), numberOfMonths - 1) < navEnd;

  /* Range pick state */

  // The pick's start; the committed value waits for the second click.
  const [pendingStart, setPendingStart] = useState<Date | null>(null);
  const [hoverDay, setHoverDay] = useState<Date | undefined>(undefined);
  const isPickingRange = variant === "range" && pendingStart !== null;

  const [focusedDay, setFocusedDayState] = useState<Date>(
    () => anchorOf(selected) ?? clampDate(today(), minDate, maxDate),
  );

  // Reset on open, so an abandoned half-pick never resumes.
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setPendingStart(null);
      setHoverDay(undefined);
      setViewMonthState(initialMonth());
      setFocusedDayState(anchorOf(selected) ?? clampDate(today(), minDate, maxDate));
    }
    wasOpen.current = open;
  }, [open, initialMonth, selected, minDate, maxDate]);

  /** Move roving focus, pulling the visible months along when focus leaves them. */
  const setFocusedDay = useCallback(
    (date: Date) => {
      setFocusedDayState(date);
      setViewMonthState((current) => {
        const visible = Array.from({ length: Math.max(1, numberOfMonths) }, (_, i) =>
          addMonths(current, i),
        );
        if (visible.some((month) => isSameMonth(month, date))) return current;
        // Off the end: focused month becomes the last pane; off the start, the first.
        const target = date < current ? startOfMonth(date) : addMonths(startOfMonth(date), -(numberOfMonths - 1));
        return clampMonth(target);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [numberOfMonths, navStart, navEnd],
  );

  /* Disabled days */

  const disabledMatchers = useMemo(
    () => composeDisabled(disabledDates, minDate, maxDate),
    [disabledDates, minDate, maxDate],
  );

  const isDisabledDay = useCallback(
    (date: Date) => isMatch(date, disabledMatchers),
    [disabledMatchers],
  );

  /* Selection */

  const timeParts = useMemo<[number, number]>(() => {
    if (isTimeOnly) {
      const parsed = parseHm(typeof selected === "string" ? selected : "");
      return parsed ? [parsed.hours, parsed.minutes] : [9, 0];
    }
    if (isValidDate(selected)) return [selected.getHours(), selected.getMinutes()];
    return [0, 0];
  }, [isTimeOnly, selected]);

  const selectDay = useCallback(
    (date: Date) => {
      if (isDisabledDay(date)) return;
      setFocusedDayState(date);

      switch (variant) {
        case "range": {
          if (pendingStart === null) {
            // First click always starts a NEW range, even over a complete one.
            setPendingStart(date);
            setHoverDay(date);
            return;
          }
          emit(orderRange(pendingStart, date));
          setPendingStart(null);
          setHoverDay(undefined);
          if (shouldCloseOnSelect) setOpen(false);
          return;
        }

        case "multiple": {
          const current = Array.isArray(selected) ? (selected as Date[]) : [];
          const exists = current.some((entry) => isSameDay(entry, date));
          const next = exists
            ? current.filter((entry) => !isSameDay(entry, date))
            : [...current, date].sort((a, b) => a.getTime() - b.getTime());
          emit(next.length > 0 ? next : undefined);
          if (shouldCloseOnSelect) setOpen(false);
          return;
        }

        case "datetime": {
          const [hours, minutes] = timeParts;
          emit(setTime(date, hours, minutes));
          if (shouldCloseOnSelect) setOpen(false);
          return;
        }

        case "time":
          return;

        case "single":
        default: {
          if (clearable && selected && isValidDate(selected) && isSameDay(selected, date)) {
            emit(undefined);
            if (shouldCloseOnSelect) setOpen(false);
            return;
          }
          emit(date);
          if (shouldCloseOnSelect) setOpen(false);
        }
      }
    },
    [
      variant,
      pendingStart,
      selected,
      timeParts,
      emit,
      isDisabledDay,
      shouldCloseOnSelect,
      setOpen,
      clearable,
    ],
  );

  const selectTime = useCallback(
    (hours: number, minutes: number) => {
      if (isTimeOnly) {
        emit(formatHm(hours, minutes));
        return;
      }
      if (variant === "datetime") {
        const base = isValidDate(selected) ? selected : clampDate(today(), minDate, maxDate);
        emit(setTime(base, hours, minutes));
      }
    },
    [variant, isTimeOnly, selected, emit, minDate, maxDate],
  );

  const applyPreset = useCallback(
    (preset: DateTimePickerPreset) => {
      const next = resolvePreset(preset, variant, weekStartsOn);
      setPendingStart(null);
      setHoverDay(undefined);
      emit(next);
      const anchor = anchorOf(next);
      if (anchor) setViewMonthState(clampMonth(startOfMonth(anchor)));
      // A preset commits everything except `multiple`, where days keep adding.
      if (variant !== "multiple") setOpen(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [variant, weekStartsOn, emit, setOpen, navStart, navEnd, numberOfMonths],
  );

  const clear = useCallback(() => {
    setPendingStart(null);
    setHoverDay(undefined);
    emit(isTimeOnly ? "" : undefined);
  }, [emit, isTimeOnly]);

  /* Derived view state */

  const preview = useMemo<InternalValue>(() => {
    if (variant === "range" && pendingStart) {
      return orderRange(pendingStart, hoverDay ?? pendingStart);
    }
    return selected;
  }, [variant, pendingStart, hoverDay, selected]);

  const getDayState = useCallback(
    (date: Date, month: Date): DayState => {
      const disabled = isDisabledDay(date);
      const outside = !isSameMonth(date, month);
      const base: DayState = {
        selected: false,
        today: isSameDay(date, new Date()),
        outside,
        disabled,
        rangeStart: false,
        rangeMiddle: false,
        rangeEnd: false,
        preview: isPickingRange,
        focused: isSameDay(date, focusedDay),
      };

      if (variant === "range") {
        const range = preview as DateRangeValue | undefined;
        if (isValidDate(range?.from)) {
          const isStart = isSameDay(date, range.from);
          const isEnd = isValidDate(range.to) && isSameDay(date, range.to);
          // Day-granular, and never an endpoint: a `to` carrying a time of day
          // would otherwise swallow its own end day, which paints as middle.
          const inside =
            !isStart &&
            !isEnd &&
            isValidDate(range.to) &&
            isAfterDay(date, range.from) &&
            isBeforeDay(date, range.to);
          return {
            ...base,
            selected: isStart || isEnd || inside,
            rangeStart: isStart,
            rangeEnd: isEnd,
            rangeMiddle: inside,
          };
        }
        return base;
      }

      if (variant === "multiple") {
        const dates = Array.isArray(preview) ? (preview as Date[]) : [];
        return { ...base, selected: dates.some((entry) => isSameDay(entry, date)) };
      }

      return { ...base, selected: isValidDate(preview) && isSameDay(date, preview) };
    },
    [variant, preview, isDisabledDay, focusedDay, isPickingRange],
  );

  const displayValue = config.format(selected, pattern, locale);
  const hasValue = isTimeOnly
    ? typeof selected === "string" && selected.length > 0
    : displayValue.length > 0;

  return {
    variant,
    config,
    selected,
    preview,
    hasValue,
    displayValue,
    formValue: toFormValue(selected, config.hasTime),
    open,
    setOpen,
    viewMonth,
    months,
    setViewMonth,
    goToMonth,
    canGoPrev,
    canGoNext,
    canGoPrevYear,
    canGoNextYear,
    navStart,
    navEnd,
    hoverDay,
    setHoverDay,
    focusedDay,
    setFocusedDay,
    isPickingRange,
    selectDay,
    selectTime,
    applyPreset,
    clear,
    isDisabledDay,
    getDayState,
    timeParts,
  };
}
